import { generateVideoThumbnailViaUrl } from '@rajesh896/video-thumbnails-generator'
import isEmail from 'validator/es/lib/isEmail'

import isBoolean from 'lodash/fp/isBoolean'
import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isString from 'lodash/fp/isString'
import snakeCase from 'lodash/fp/snakeCase'
import trimCharsEnd from 'lodash/fp/trimCharsEnd'

import {
  DEFAULT_APP_NAME,
  PREPROD_DOMAIN,
  SNOWBALL_MEDIA_DOMAIN,
} from '../constants'
import { Apps, BooleanValues } from '../enums'

export { default as isEmail } from 'validator/es/lib/isEmail'

/** @public */
export function removeLocaleFromUrl(url: string | null | undefined) {
  if (isEmpty(url)) {
    return ''
  }

  return url.replace(/^\/([A-Za-z]{2}(-[A-Za-z]{2})?)\//, '/')
}

/** @public */
export function marshallBoolean(value: unknown) {
  if (!isBoolean(value)) {
    return value
  }

  if (value) {
    return BooleanValues.True
  }

  return BooleanValues.False
}

/** @public */
export function unmarshallBoolean<T = unknown>(value: T): boolean | T {
  if (!isString(value) || isEmpty(value)) {
    return value
  }

  if (value === BooleanValues.True) {
    return true
  }

  if (value === BooleanValues.False) {
    return false
  }

  return value
}

/** @public */
export async function sleep(duration: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, duration)
  })
}

export function wait(duration: number) {
  const start = Date.now()
  while (Date.now() - start < duration) {
    // Busy wait.
  }
}
export const syncSleep = wait

/** @public */
export function createSingleton<T>(name: string, create: () => T): T {
  const s = Symbol.for(name)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let scope = (global as any)[s]
  if (!scope) {
    scope = { ...create() }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(global as any)[s] = scope
  }

  return scope
}

/** @public */
export function env() {
  return (
    process.env.VERCEL_ENV ||
    process.env.NEXT_PUBLIC_VERCEL_ENV ||
    process.env.NODE_ENV ||
    process.env.NEXT_PUBLIC_NODE_ENV
  )
}

/** @public */
export function isProd() {
  return env() === 'production'
}

/** @public */
export function isPreview() {
  return env() === 'preview'
}

/** @public */
export function isDev() {
  return env() === 'development'
}

/** @public */
export function uuidV4() {
  /* eslint-disable no-bitwise */
  return '10000000-1000-4000-8000-100000000000'.replaceAll(/[018]/g, (char) => {
    const charValue = Number.parseInt(char, 10)

    return (
      charValue ^
      ((crypto.getRandomValues(new Uint8Array(1))[0] ?? 0) &
        (15 >> (charValue / 4)))
    ).toString(16)
  })
  /* eslint-enable no-bitwise */
}

/** @public */
export function slugify(string: string) {
  return snakeCase(string.normalize().replaceAll(/["'`]/g, '_')).replaceAll(
    /[^\d_a-z]/g,
    '',
  )
}

/** @public */
export function computeRootHost(host: string | null) {
  if (isEmpty(host)) {
    return undefined
  }

  const splittedReversedHost = host.split('.').reverse()
  const hostToKeep = host.includes('preprod.snowball.xyz')
    ? splittedReversedHost.slice(0, 3)
    : splittedReversedHost.slice(0, 2)

  return hostToKeep.reverse().join('.')
}

/** @public */
export function getRootHost(withPort = false) {
  const headers =
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    typeof window === 'undefined' ? require('next/headers').headers() : null

  const host =
    typeof window === 'undefined' ? headers.get('host') : window.location.host

  return computeRootHost(withPort ? host : host!.split(':')[0]!)
}

/** @public */
export function getPort() {
  const headers =
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    typeof window === 'undefined' ? require('next/headers').headers() : null

  const host =
    typeof window === 'undefined' ? headers.get('host') : window.location.host

  return String(host.split(':')[1] ?? '')
}

/** @public */
export function getAppUrlInfo() {
  const headers =
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    typeof window === 'undefined' ? require('next/headers').headers() : null

  const domain =
    typeof window === 'undefined'
      ? headers.get('x-forwarded-host')
      : window.location.hostname
  const protocol =
    (typeof window === 'undefined'
      ? headers.get('x-forwarded-proto')
      : window.location.protocol) || 'https'
  const host = getRootHost()
  const port = getPort()

  const splittedReversedDomain = domain?.split('.').reverse()

  let appName =
    process.env.APP_NAME ||
    process.env.NEXT_PUBLIC_APP_NAME ||
    (domain.includes('preprod.snowball.xyz')
      ? splittedReversedDomain[3]
      : splittedReversedDomain[2])
  appName =
    isEmpty(appName) ||
    appName === 'www' ||
    appName === 'content' ||
    appName === 'alpha' ||
    appName === 'beta'
      ? DEFAULT_APP_NAME
      : appName

  return {
    appName,
    domain,
    host,
    port,
    protocol: trimCharsEnd(':', protocol ?? 'https'),
  }
}

/** @public */
export function isLocalDev() {
  const { domain, protocol } = getAppUrlInfo()

  return (
    isEmpty(domain) ||
    domain.includes(`local-snowball.xyz`) ||
    domain.startsWith('localhost') ||
    domain.startsWith('0.0.0.0') ||
    domain.startsWith('127.0.0.1') ||
    domain.startsWith('192.168.') ||
    domain.startsWith('172.') ||
    domain.startsWith('10.') ||
    isEmpty(protocol)
  )
}

/** @public */
export function shouldUseApiApp() {
  return (
    (process.env.USE_API_APP || process.env.NEXT_PUBLIC_USE_API_APP) === 'true'
  )
}

/** @public */
export function getProtocolForLocalDev() {
  return (
    process.env.REMOTE_PROTOCOL ||
    process.env.NEXT_PUBLIC_REMOTE_PROTOCOL ||
    'https'
  )
}

/** @public */
export function getApiDomain(forPreprod = false) {
  const { appName, domain } = getAppUrlInfo()

  const isCurrentlyPreprod =
    domain?.includes('preprod-snowball.xyz') ||
    domain?.includes('preprod.snowball.xyz') ||
    domain?.includes('alpha.snowball.xyz') ||
    (domain?.includes('snowball-tech.vercel.app') &&
      !domain?.includes('git-prod-snowball-tech.vercel.app'))

  const currentPreprodDomain = isCurrentlyPreprod
    ? domain?.includes('preprod-snowball.xyz')
      ? 'preprod-snowball.xyz'
      : PREPROD_DOMAIN
    : PREPROD_DOMAIN

  const preprodApiDomain = shouldUseApiApp()
    ? `api.${currentPreprodDomain}`
    : `${appName === Apps.Media ? '' : `${appName}.`}${currentPreprodDomain}`
  const prodApiDomain = shouldUseApiApp() ? 'api.snowball.xyz' : domain

  return forPreprod || isCurrentlyPreprod ? preprodApiDomain : prodApiDomain
}

/** @public */
export function getApiDomainForLocal() {
  return (
    process.env.REMOTE_API_URL ||
    process.env.NEXT_PUBLIC_REMOTE_API_URL ||
    getApiDomain(true)
  )
}

/** @public */
export function maxHeightToViewport(height: number, ratio = 2) {
  const actualRatio = ratio <= 0 ? 1 : ratio

  const viewportHeight = Math.max(
    document.documentElement.clientHeight || 0,
    window.innerHeight || 0,
  )

  return Math.min(height, viewportHeight / actualRatio)
}

/** @public */
export function maxWidthToViewport(width: number, ratio = 2) {
  const actualRatio = ratio <= 0 ? 1 : ratio

  const viewportWidth = Math.max(
    document.documentElement.clientWidth || 0,
    window.innerWidth || 0,
  )

  return Math.min(width, viewportWidth / actualRatio)
}

/** @public */
export function maxWidthToContainer(
  container: Element,
  width: number,
  ratio = 2,
) {
  const actualRatio = ratio <= 0 ? 1 : ratio

  const containerWidth = container.clientWidth || 0

  return maxWidthToViewport(Math.min(width, containerWidth / actualRatio), 1)
}

/** @public */
export function getFullMediaUrl(
  path: string,
  baseUrl = process.env.SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL,
) {
  return `${baseUrl}/storage/v1/object/public/${path}`
}

/** @public */
export function joinWithLast(
  array: Array<unknown> | null | undefined,
  joinWith = ',',
  lastJoinWith = '',
  filter = true,
) {
  const filteredArray =
    (filter ? array?.filter((value) => !isEmpty(value)) : array) ?? []

  if (isEmpty(filteredArray)) {
    return ''
  }

  if (filteredArray.length === 1) {
    return String(filteredArray[0])
  }

  if (isEmpty(lastJoinWith) || lastJoinWith === joinWith) {
    return filteredArray.join(joinWith)
  }

  return `${filteredArray.slice(0, -1).join(joinWith)}${lastJoinWith}${filteredArray.at(-1)}`
}

/** @public */
export function stopPropagation(immediate = false, preventDefaultToo = false) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (event: any) => {
    if (immediate) {
      if (
        Object.hasOwn(event, 'nativeEvent') &&
        Object.hasOwn(event?.nativeEvent, 'stopImmediatePropagation') &&
        isFunction(event?.nativeEvent?.stopImmediatePropagation)
      ) {
        event?.nativeEvent?.stopImmediatePropagation()
      } else if (
        Object.hasOwn(event, 'stopImmediatePropagation') &&
        isFunction(event?.stopImmediatePropagation)
      ) {
        event?.stopImmediatePropagation()
      }
    } else {
      event?.stopPropagation()
    }

    if (
      preventDefaultToo &&
      Object.hasOwn(event, 'preventDefault') &&
      isFunction(event.preventDefault)
    ) {
      event?.preventDefault()
    }
  }
}

/** @public */
export function preventDefault(
  stopImmediatePropagation = false,
  stopPropagationToo = false,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (event: any) => {
    if (stopPropagationToo || stopImmediatePropagation) {
      stopPropagation(stopImmediatePropagation, true)(event)

      return
    }

    if (
      Object.hasOwn(event, 'preventDefault') &&
      isFunction(event.preventDefault)
    ) {
      event?.preventDefault()
    }
  }
}

/** @public */
export function stringToColor(string: string) {
  let hash = 0
  ;[...string].forEach((char) => {
    // eslint-disable-next-line no-bitwise
    hash = char.codePointAt(0) ?? 0 + ((hash << 5) - hash)
  })

  let color = '#'
  for (let index = 0; index < 3; index += 1) {
    // eslint-disable-next-line no-bitwise
    const value = (hash >> (index * 8)) & 0xff
    color += value.toString(16).padStart(2, '0')
  }

  return color
}

/** @public */
export async function getVideoThumbnail(url: string, number = 1) {
  const base64Thumbnail = await generateVideoThumbnailViaUrl(url, number)

  const fileName = `${url.slice(Math.max(0, url.lastIndexOf('/') + 1))}.jpg`

  const splittedBase64 = base64Thumbnail.split(',')
  const mime = splittedBase64[0]?.match(/:(.*?);/)?.[1]
  const byteString = splittedBase64[0]?.includes('base64')
    ? atob(splittedBase64[1]!)
    : decodeURI(splittedBase64[1]!)

  if (!isEmpty(byteString)) {
    const ia = new Uint8Array(byteString.length)
    for (let index = 0; index < byteString.length; index += 1)
      ia[index] = byteString.charCodeAt(index)

    return { name: fileName, thumbnail: new Blob([ia], { type: mime }) }
  }

  return null
}

/** @public */
export function isValidEmail(email: string | null | undefined) {
  return !isEmpty(email) && isEmail(email)
}

/** @public */
export function getReturnUrl(
  url: string | null | undefined,
  defaultUrl?: string,
) {
  let returnUrl = url || defaultUrl
  if (
    returnUrl === 'undefined' ||
    returnUrl === 'null' ||
    isEmpty(returnUrl) ||
    !isString(returnUrl)
  ) {
    returnUrl = defaultUrl
  }
  if (
    !isEmpty(returnUrl) &&
    !returnUrl.startsWith('http') &&
    !returnUrl.startsWith('/')
  ) {
    returnUrl = `/${returnUrl}`
  }

  return returnUrl
}

/** @public */
export function getFullQualifiedUrl(
  url: string | null | undefined,
  { keepAbsoluteUrl = true } = {},
) {
  if (
    isEmpty(url) ||
    url.startsWith('http') ||
    (keepAbsoluteUrl && url.startsWith('/'))
  ) {
    return url || undefined
  }

  return `https://${url}`
}

export function getRedirectUrl(
  url: string | null | undefined,
  { isSponsor = false, keepAbsoluteUrl = true } = {},
) {
  return `https://redirect.${SNOWBALL_MEDIA_DOMAIN}/?to=${isEmpty(url) ? '' : encodeURIComponent((getFullQualifiedUrl(url, { keepAbsoluteUrl }) ?? url)?.replaceAll('snowball.xyz', '__SNOWBALL__'))}${isSponsor ? '&isSnowballSponsor' : ''}`
}

export function uncapitalize(string_: string) {
  return string_.charAt(0).toLowerCase() + string_.slice(1)
}
